import styled from 'styled-components'
import { useRouter } from 'next/router'
import { useProjectsInProgress, usePendingProjects } from 'querys/projects-plan'
import { Typography } from 'common/typography'
import { useLearningStats } from 'querys/course'
import useIntersectionObserver from 'hooks/use-intersection-observer'
import PodiumBar from './podium-bar'
import ProjectsFilter from './projects-filter'
import PlanProjectItem from './plan-project-item'

const LearningPlanProjectsStyled = styled.div`
  padding-block-end: 4rem;
  .turquoise {
    color: var(--turquo)
  }
  .projects-progress {
    margin-block-start: 0rem;
    margin-block-end: 3rem;
  }
  .projects-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .projects-list {
    margin-block-start: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 64px;
  }
  .projects-progress-bar {
    margin-block-start: .5rem;
    block-size: .5rem;
    background: #10fefe33;
    position: relative;
    &:after {
      border-radius: 1rem;
      position: absolute;
      background: var(--turquoise);
      content: "";
      height: .5rem;
      right: ${({ progress }) => `${100 - progress}%`};
      left: 0;
    }
  }
  @media (min-witdh: 768px) {
    .projects-list {
      gap: 80px;
    }
  }
  @media (min-witdh: 0px) {
    padding-block-end: 0;
  }
`


function AdvancedProjects() {
  const defaultParams = { level: 'ADVANCED' }
  const { data, fetchNextPage, refetch } = usePendingProjects(defaultParams)
  const { ref: lastProject } = useIntersectionObserver({
    onIntersect: () => fetchNextPage(),
    rootMargin: '0px 0px 500px 0px',
  })

  return (
    <>
      {data?.pages?.map(({ results }) => (
        results?.map((item) => <PlanProjectItem key={item.uuid} refetch={refetch} {...item} />)
      ))}
      <div ref={lastProject} />
    </>
  )
}

function IntermediateProjects() {
  const defaultParams = { level: 'INTERMEDIATE' }
  const { data, fetchNextPage, refetch } = usePendingProjects(defaultParams)
  const { ref: lastProject } = useIntersectionObserver({
    onIntersect: () => fetchNextPage(),
    rootMargin: '0px 0px 500px 0px',
  })

  return (
    <>
      {data?.pages?.map(({ results }) => (
        results?.map((item) => <PlanProjectItem key={item.uuid} refetch={refetch} {...item} />)
      ))}
      {data?.pages?.length * 10 > data?.pages[0]?.count ? (
        <AdvancedProjects />
      ) : null}
      <div ref={lastProject} />
    </>
  )
}

function LearningPlanProjects() {
  const { query } = useRouter()
  const defaultParams = Object.keys(query).length > 0 ? {} : { level: 'BASIC' }
  const { data: projectsInProgress } = useProjectsInProgress()
  const { data, fetchNextPage, refetch } = usePendingProjects(defaultParams)
  const { data: stats } = useLearningStats()

  const { ref: lastProject } = useIntersectionObserver({
    onIntersect: () => fetchNextPage(),
    rootMargin: '0px 0px 500px 0px',
  })

  return (
    <LearningPlanProjectsStyled progress={(stats?.projects_completed * 100) / 109}>
      <div className="projects-progress">
        <div className="projects-title">
          <Typography as="h1" small="regularBold" large="headline5">
            Proyectos en tu portafolio
          </Typography>
          <Typography as="span" small="regularBold" large="headline5" color="var(--turquoise)">
            {stats?.projects_completed || 0}
            / 109
          </Typography>
        </div>
        <div className="projects-progress-bar" />
      </div>
      <PodiumBar />
      <div className="projects-title">
        <Typography font="headline6">
          Tu siguiente reto
        </Typography>
        <ProjectsFilter />
      </div>
      <ul className="projects-list">
        {projectsInProgress?.results?.map((item) => (
          <PlanProjectItem key={item.uuid} refetch={refetch} {...item} />
        ))}
        {data?.pages?.map(({ results }) => (
            results?.map((item) => <PlanProjectItem key={item.uuid} refetch={refetch} {...item} />)
        ))}
        {data?.pages?.length * 10 > data?.pages[0]?.count && Object.keys(query).length === 0 ? (
          <IntermediateProjects />
        ) : null}
        <div ref={lastProject} />
      </ul>
    </LearningPlanProjectsStyled>
  )
}

export default LearningPlanProjects
